import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Subscription } from 'rxjs';
import { EchartGenericChartComponent } from '@/app/charts/echart-generic-chart/echart-generic-chart.component';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { ThemeService } from '@/app/services/theme/theme.service';
import {
  formatStatsPeriodLabel,
} from '../../../admin-stats-period/admin-stats-period.helpers';
import {
  getKpiTrend,
  kpiAccentDotClass,
} from '../../../admin-stats-dashboard/admin-stats-dashboard.helpers';
import {
  StatsChartVariant,
  StatsKpiCardModel,
} from '../../../admin-stats-aggregate/admin-stats-aggregate.types';
import { AdminReportsService } from '../../../../services/admin-reports.service';
import { AdminReportsPeriodFilter } from '../reports-period-filter/reports-period-filter';
import { buildPeriodComparisonBarChart, resolveComparisonPeriod } from '../../reports.helpers';
import { ReportMetricsDto, ReportsPeriodPreset, ReportsPeriodSelection } from '../../reports.types';
import { EChartsOption } from 'echarts';

@Component({
  selector: 'admin-tool-reports-dashboard',
  imports: [AdminReportsPeriodFilter, LoadingInlineComponent, EchartGenericChartComponent],
  templateUrl: './reports-dashboard.html',
  styleUrl: './reports-dashboard.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportsDashboard implements OnDestroy {
  metrics: ReportMetricsDto | null = null;
  previousMetrics: ReportMetricsDto | null = null;
  kpiCards: StatsKpiCardModel[] = [];
  comparisonChartOption: EChartsOption | null = null;
  comparisonChartXaxisData: string[] = [];
  comparisonChartHasData = false;
  comparisonChartVariant: StatsChartVariant = 'timelineBarPurple';
  rangeLabel = '';
  previousRangeLabel = '';
  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  savingState: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  error: string | null = null;
  currentStartDate = '';
  currentEndDate = '';

  readonly kpiAccentDotClass = kpiAccentDotClass;
  readonly getKpiTrend = getKpiTrend;

  private subscriptionList = new Subscription();
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly adminReportsService = inject(AdminReportsService);
  private readonly toastNotificationService = inject(ToastNotificationService);
  private readonly themeService = inject(ThemeService);

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onPeriodChange(selection: ReportsPeriodSelection): void {
    this.loadReport(selection.startDate, selection.endDate, selection.preset);
  }

  saveReport(): void {
    if (!this.currentStartDate || !this.currentEndDate || this.savingState === 'saving') {
      return;
    }

    this.savingState = 'saving';
    this.refreshView();

    this.subscriptionList.add(
      this.adminReportsService.save(this.currentStartDate, this.currentEndDate).subscribe({
        next: () => {
          this.savingState = 'saved';
          this.refreshView();
          void this.toastNotificationService.alert({
            title: 'Report saved',
            message: `Saved report for ${this.rangeLabel}.`,
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: unknown) => {
          this.savingState = 'error';
          this.refreshView();
          void this.toastNotificationService.alert({
            title: 'Could not save report',
            message: this.resolveSaveErrorMessage(err),
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  private loadReport(startDate: string, endDate: string, preset: ReportsPeriodPreset): void {
    this.subscriptionList.unsubscribe();
    this.subscriptionList = new Subscription();

    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    this.rangeLabel = formatStatsPeriodLabel(startDate, endDate);
    this.loadingState = 'loading';
    this.savingState = 'idle';
    this.error = null;
    this.refreshView();

    const previous = resolveComparisonPeriod(startDate, endDate, preset);
    this.previousRangeLabel = formatStatsPeriodLabel(previous.startDate, previous.endDate);

    this.subscriptionList.add(
      forkJoin({
        current: this.adminReportsService.generate(startDate, endDate),
        previous: this.adminReportsService.generate(previous.startDate, previous.endDate),
      }).subscribe({
        next: ({ current, previous: previousMetrics }) => {
          this.metrics = current;
          this.previousMetrics = previousMetrics;
          this.kpiCards = this.buildKpiCards(current, previousMetrics);
          this.buildComparisonChart(current, previousMetrics);
          this.loadingState = 'loaded';
          this.refreshView();
        },
        error: () => {
          this.metrics = null;
          this.previousMetrics = null;
          this.kpiCards = [];
          this.comparisonChartOption = null;
          this.comparisonChartXaxisData = [];
          this.comparisonChartHasData = false;
          this.loadingState = 'error';
          this.error = 'Failed to generate report for the selected period.';
          this.refreshView();
        },
      }),
    );
  }

  private buildKpiCards(
    current: ReportMetricsDto,
    previous: ReportMetricsDto,
  ): StatsKpiCardModel[] {
    return [
      {
        key: 'creatorsJoined',
        label: 'Creators joined',
        current: current.creatorsJoined,
        previous: previous.creatorsJoined,
        accent: 'violet',
      },
      {
        key: 'briefsPosted',
        label: 'Briefs posted',
        current: current.briefsPosted,
        previous: previous.briefsPosted,
        accent: 'pink',
      },
      {
        key: 'creatorsHired',
        label: 'Creators hired',
        current: current.creatorsHired,
        previous: previous.creatorsHired,
        accent: 'violet',
      },
      {
        key: 'brandsJoined',
        label: 'Brands joined',
        current: current.brandsJoined,
        previous: previous.brandsJoined,
        accent: 'pink',
      },
    ];
  }

  private buildComparisonChart(current: ReportMetricsDto, previous: ReportMetricsDto): void {
    const isDark = this.themeService.isDarkMode();
    const built = buildPeriodComparisonBarChart(current, previous, isDark);
    this.comparisonChartOption = built.chartOption;
    this.comparisonChartXaxisData = built.chartXaxisData;
    this.comparisonChartHasData = built.hasData;
    this.comparisonChartVariant = isDark ? 'timelineBarDark' : 'timelineBarPurple';
  }

  private resolveSaveErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const message = err.error?.message;
      if (typeof message === 'string' && message.length > 0) {
        return message;
      }
      if (Array.isArray(message) && typeof message[0] === 'string') {
        return message[0];
      }
    }

    return 'Unable to save this report. Please try again.';
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }
}
